import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { MaterialIcon } from '../ui/MaterialIcon';
import type { EquipmentFormState } from './addEquipmentForm';

interface AddEquipmentFormStepProps {
  readonly form: EquipmentFormState;
  readonly onFieldChange: (field: keyof EquipmentFormState, value: string) => void;
  /** Set when a nameplate scan filled the form. */
  readonly scanConfidence: number | undefined;
  readonly isSubmitting: boolean;
  readonly isBusy: boolean;
  readonly onSubmit: () => void;
  readonly onBack: () => void;
}

interface FieldProps {
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}

function Field({ label, placeholder, value, onChange }: FieldProps) {
  return (
    <FormControl>
      <FormLabel fontSize="sm">{label}</FormLabel>
      <Input
        size="sm"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </FormControl>
  );
}

/** The equipment details, typed or pre-filled by a scan or search result. */
export function AddEquipmentFormStep({
  form,
  onFieldChange,
  scanConfidence,
  isSubmitting,
  isBusy,
  onSubmit,
  onBack,
}: AddEquipmentFormStepProps) {
  const bind = (field: keyof EquipmentFormState) => ({
    value: form[field],
    onChange: (value: string) => onFieldChange(field, value),
  });
  return (
    <Flex direction="column" flex={1} minH={0}>
      {/* The fields scroll; the action row stays pinned below them. `p`/`m`
          keep focus rings inside the scroller's clip. */}
      <VStack spacing={4} align="stretch" flex={1} minH={0} overflowY="auto" p={1} m={-1}>
        {scanConfidence !== undefined && (
          <Box
            p={3}
            borderRadius="md"
            bg="bg.subtle"
            borderWidth="1px"
            borderColor="border.default"
          >
            <HStack spacing={2}>
              <MaterialIcon name="photo_camera" boxSize="16px" color="text.secondary" />
              <Text fontSize="sm" color="text.secondary">
                Scan confidence: {Math.round(scanConfidence * 100)}%
              </Text>
            </HStack>
          </Box>
        )}
        <FormControl isRequired>
          <FormLabel fontSize="sm">
            Name
            <Text as="span" fontSize="xs" color="text.muted" fontWeight="normal" ml={2}>
              A label to identify this instrument in your lab
            </Text>
          </FormLabel>
          <Input
            size="sm"
            placeholder="e.g. Centrifuge: lab 3B"
            value={form.title}
            onChange={(e) => onFieldChange('title', e.target.value)}
            autoFocus
          />
        </FormControl>
        <HStack spacing={3}>
          <Field label="Make" placeholder="e.g. Eppendorf" {...bind('make')} />
          <Field label="Model" placeholder="e.g. 5810 R" {...bind('model')} />
        </HStack>
        <HStack spacing={3}>
          <Field label="Serial number" placeholder="e.g. 5811R12345" {...bind('serial')} />
          <Field label="Location" placeholder="e.g. Lab 3B" {...bind('location')} />
        </HStack>
      </VStack>
      <HStack justify="flex-end" spacing={2} pt={4} flexShrink={0}>
        <Button variant="outline" size="sm" onClick={onBack}>
          Back
        </Button>
        <Button
          size="sm"
          onClick={onSubmit}
          isLoading={isSubmitting}
          isDisabled={isBusy || !form.title.trim()}
        >
          Add Equipment
        </Button>
      </HStack>
    </Flex>
  );
}
